import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Grid,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material'
import GroupIcon from '@mui/icons-material/Group'
import SendIcon from '@mui/icons-material/Send'
import BusinessIcon from '@mui/icons-material/Business'
import { itsmProductApi, productDlApi } from '../utils/api'

const ProductOnboard = ({ prePopulatedProductName = null, onComplete = null }) => {
  const [formData, setFormData] = useState({
    productName: prePopulatedProductName || '',
    productTeamDL: '',
    developmentTeamDL: '',
    sreTeamDL: '',
    additionalDL: '',
  })

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [showAdditionalDL, setShowAdditionalDL] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [errors, setErrors] = useState({
    productTeamDL: '',
    developmentTeamDL: '',
    sreTeamDL: '',
    additionalDL: '',
  })

  // Fetch products from API
  useEffect(() => {
    fetchProducts()
  }, [])

  // Update formData when prePopulatedProductName changes
  useEffect(() => {
    if (prePopulatedProductName) {
      setFormData(prev => ({ ...prev, productName: prePopulatedProductName }))
    }
  }, [prePopulatedProductName])

  const fetchProducts = async (search = '') => {
    setLoadingProducts(true)
    try {
      const response = await itsmProductApi.getAll({ search, limit: 100 })
      setProducts(response.data.items || [])
    } catch (error) {
      console.error('Error fetching products:', error)
      setSnackbar({ open: true, message: 'Failed to load products', severity: 'error' })
    } finally {
      setLoadingProducts(false)
    }
  }

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== undefined) {
        fetchProducts(searchText)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchText])

  const validateEmailList = (emailString) => {
    if (!emailString || emailString.trim() === '') return { valid: true, message: '' }
    
    const emails = emailString.split(',').map(email => email.trim())
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    for (let email of emails) {
      if (!emailRegex.test(email)) {
        return { valid: false, message: `Invalid email format: ${email}` }
      }
    }
    
    return { valid: true, message: '' }
  }

  const handleDLChange = (field, value) => {
    setFormData({ ...formData, [field]: value })
    
    // Validate on change
    const validation = validateEmailList(value)
    setErrors({ ...errors, [field]: validation.message })
  }
  

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.productName || !formData.productTeamDL || !formData.developmentTeamDL || !formData.sreTeamDL) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' })
      return
    }

    // Validate email format for distribution lists
    const productTeamValidation = validateEmailList(formData.productTeamDL)
    if (!productTeamValidation.valid) {
      setSnackbar({ open: true, message: `Product Team DL: ${productTeamValidation.message}`, severity: 'error' })
      return
    }

    const devTeamValidation = validateEmailList(formData.developmentTeamDL)
    if (!devTeamValidation.valid) {
      setSnackbar({ open: true, message: `Development Team DL: ${devTeamValidation.message}`, severity: 'error' })
      return
    }

    const sreTeamValidation = validateEmailList(formData.sreTeamDL)
    if (!sreTeamValidation.valid) {
      setSnackbar({ open: true, message: `SRE Team DL: ${sreTeamValidation.message}`, severity: 'error' })
      return
    }

    if (formData.additionalDL) {
      const additionalValidation = validateEmailList(formData.additionalDL)
      if (!additionalValidation.valid) {
        setSnackbar({ open: true, message: `Additional DL: ${additionalValidation.message}`, severity: 'error' })
        return
      }
    }

    try {
      // Find the selected product to get its product_code
      const selectedProduct = products.find(p => p.product_name === formData.productName)
      if (!selectedProduct) {
        setSnackbar({ open: true, message: 'Please select a valid product', severity: 'error' })
        return
      }

      // Prepare data for API
      const productDlData = {
        product_code: selectedProduct.product_code,
        product_team_dl: formData.productTeamDL,
        development_team_dl: formData.developmentTeamDL,
        sre_team_dl: formData.sreTeamDL,
        additional_dl: formData.additionalDL || null,
      }

      // Submit to backend
      await productDlApi.create(productDlData)
      
      setSnackbar({ open: true, message: 'ITSM Product onboarded successfully!', severity: 'success' })
      
      // Call onComplete callback if provided (used when in modal)
      if (onComplete) {
        // Close modal immediately after success
        setTimeout(() => {
          onComplete(selectedProduct)
        }, 800)
      } else {
        // Only reset form if NOT in modal (standalone page)
        setTimeout(() => {
          setFormData({
            productName: '',
            productTeamDL: '',
            developmentTeamDL: '',
            sreTeamDL: '',
            additionalDL: '',
          })
          setErrors({
            productTeamDL: '',
            developmentTeamDL: '',
            sreTeamDL: '',
            additionalDL: '',
          })
          setShowAdditionalDL(false)
        }, 2000)
      }
    } catch (error) {
      console.error('Error onboarding product:', error)
      const errorMessage = error.response?.data?.detail || 'Error onboarding product. Please try again.'
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    }
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {!prePopulatedProductName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Onboarding ITSM Products
          </Typography>
        </Box>
      )}

      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Register your ITSM product and associated team distribution lists for certificate management notifications and approvals.
        </Alert>

        <form onSubmit={handleSubmit}>
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Product Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Autocomplete
                fullWidth
                disabled={!!prePopulatedProductName}
                options={products}
                getOptionLabel={(option) => option.product_name || ''}
                value={products.find(p => p.product_name === formData.productName) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, productName: newValue?.product_name || '' })
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchText(newInputValue)
                }}
                loading={loadingProducts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Name *"
                    placeholder="Search and select product"
                    helperText={prePopulatedProductName ? "Product pre-selected for onboarding" : "Search for your ITSM product"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.product_id}>
                    <Box>
                      <Typography variant="body1">{option.product_name}</Typography>
                      {option.product_code && (
                        <Typography variant="caption" color="text.secondary">
                          Code: {option.product_code}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                sx={{ mb: 3 }}
              />
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon color="primary" />
                Team Distribution Lists
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Provide email distribution lists for all teams involved in certificate management. Use comma-separated email addresses for multiple contacts (e.g., team1@company.com, team2@company.com).
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Product Team DL *"
                    value={formData.productTeamDL}
                    onChange={(e) => handleDLChange('productTeamDL', e.target.value)}
                    placeholder="product-team@broadridge.com, manager@broadridge.com"
                    helperText={errors.productTeamDL || "Main product team contact (comma-separated for multiple emails)"}
                    error={!!errors.productTeamDL}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Development Team DL *"
                    value={formData.developmentTeamDL}
                    onChange={(e) => handleDLChange('developmentTeamDL', e.target.value)}
                    placeholder="dev-team@broadridge.com, lead-dev@broadridge.com"
                    helperText={errors.developmentTeamDL || "Development team for technical coordination (comma-separated for multiple emails)"}
                    error={!!errors.developmentTeamDL}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="SRE Team DL *"
                    value={formData.sreTeamDL}
                    onChange={(e) => handleDLChange('sreTeamDL', e.target.value)}
                    placeholder="sre-team@broadridge.com, oncall@broadridge.com"
                    helperText={errors.sreTeamDL || "Site Reliability Engineering team for operations (comma-separated for multiple emails)"}
                    error={!!errors.sreTeamDL}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAdditionalDL}
                        onChange={(e) => {
                          setShowAdditionalDL(e.target.checked)
                          if (!e.target.checked) {
                            setFormData({ ...formData, additionalDL: '' })
                            setErrors({ ...errors, additionalDL: '' })
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Add Additional Distribution List (Optional)
                      </Typography>
                    }
                  />
                </Grid>

                {showAdditionalDL && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional DL"
                      value={formData.additionalDL}
                      onChange={(e) => handleDLChange('additionalDL', e.target.value)}
                      placeholder="additional-team@broadridge.com, support@broadridge.com"
                      helperText={errors.additionalDL || "Optional: Additional team distribution list (comma-separated for multiple emails)"}
                      error={!!errors.additionalDL}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setFormData({
                  productName: '',
                  productTeamDL: '',
                  developmentTeamDL: '',
                  sreTeamDL: '',
                  additionalDL: ''
                });
                setErrors({
                  productTeamDL: '',
                  developmentTeamDL: '',
                  sreTeamDL: '',
                  additionalDL: ''
                });
                setShowAdditionalDL(false);
              }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<SendIcon />}
            >
              Submit Onboarding
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProductOnboard
